import React, { useEffect, useState } from 'react';
import FormTabs from './FormTabs';
import PaymentModalView from './PaymentModalView';
import FormSecurityNotice from './FormSecurityNotice';
import { getAuthConfig } from '../Utils/authConfig';
import { extractCleanDomain, isValidDomainFormat, calculateDomainLengthPrices } from '../Utils/domainExtractor';
import { 
    CrossIcon,
    ZapIcon, 
    WarningIcon, 
    GlobeIcon, 
    BuyIcon, 
    RentIcon, 
    ForwardIcon, 
    RobotIcon, 
    CheckCircleIcon, 
    ExternalLinkIcon, 
    MessageIcon,
    WandIcon
} from './Icons';

const ContactForm = ({ 
    domain, 
    onClose, 
    isPurchase = false, 
    onSwitchToRent, 
    onSwitchToForward, 
    onSubmitted,
    currentUser,
    authConfigProp
}) => {
    // Get Telegram contact link and bot username from environment variable
    const telegramBotUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME || '';
    const telegramContactLink = process.env.REACT_APP_TELEGRAM_CONTACT || (telegramBotUsername ? `https://t.me/${telegramBotUsername}` : 'https://t.me');
    const [showPaymentOptions, setShowPaymentOptions] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // Handle closing the modal with Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        // Add event listeners
        document.addEventListener('keydown', handleEscape);

        // Cleanup event listeners
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose]);

    // Reset state only if user switched to a completely different domain
    const prevDomainIdRef = React.useRef(domain?.id);
    useEffect(() => {
        if (prevDomainIdRef.current !== domain?.id) {
            prevDomainIdRef.current = domain?.id;
            setShowPaymentOptions(false);
            setInvoiceData(null);
            setSubmitError(null);
        }
    }, [domain?.id]);

    const [invoiceData, setInvoiceData] = useState(null);
    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

    const handleGenerateInvoice = async () => {
        setIsGeneratingInvoice(true);
        setSubmitError(null);
        try {
            const userData = JSON.parse(localStorage.getItem('telegramUser') || '{}');
            const res = await fetch('/api/create-crypto-invoice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'buy',
                    domainId: domain?.id,
                    userId: userData?.id || 1
                })
            });
            const data = await res.json();
            if (data.success && data.invoice) {
                setInvoiceData(data.invoice);
                setShowPaymentOptions(true);
            } else {
                setSubmitError(data.error || 'Failed to generate payment invoice.');
            }
        } catch (error) {
            console.error('Error generating invoice:', error);
            setSubmitError('Failed to generate payment invoice. Please try again.');
        } finally {
            setIsGeneratingInvoice(false);
        }
    };

    const [authConfig, setAuthConfig] = useState(() => {
        if (authConfigProp && (authConfigProp.success || authConfigProp.authEnabled !== undefined)) {
            return authConfigProp;
        }
        return { 
            authEnabled: true, 
            minPrices: { buy: 100, rent: 50, forward: 10 }, 
            botUsername: '' 
        };
    });
    const [authConfigLoaded, setAuthConfigLoaded] = useState(Boolean(authConfigProp?.success || authConfigProp?.authEnabled !== undefined));

    useEffect(() => {
        if (authConfigProp && (authConfigProp.success || authConfigProp.authEnabled !== undefined)) {
            setAuthConfig(authConfigProp);
            setAuthConfigLoaded(true);
        }
    }, [authConfigProp]);

    const [userToken, setUserToken] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('token');
        }
        return null;
    });

    useEffect(() => {
        const syncToken = () => {
            if (typeof window !== 'undefined') {
                setUserToken(localStorage.getItem('token'));
            }
        };
        syncToken();
        window.addEventListener('domainer_auth_change', syncToken);
        window.addEventListener('storage', syncToken);
        return () => {
            window.removeEventListener('domainer_auth_change', syncToken);
            window.removeEventListener('storage', syncToken);
        };
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setUserToken(localStorage.getItem('token'));
        }
    }, [currentUser]);

    const [submittingDomain, setSubmittingDomain] = useState(false);
    const [isAutoFilling, setIsAutoFilling] = useState(false);
    const [aiNotice, setAiNotice] = useState('');
    const [subSuccess, setSubSuccess] = useState(false);
    const [subError, setSubError] = useState('');
    const [domainFieldError, setDomainFieldError] = useState('');
    const [domainShake, setDomainShake] = useState(false);
    const domainInputRef = React.useRef(null);
    const [subForm, setSubForm] = useState({
        domain_name: '',
        description: '',
        enable_buy: true,
        pricebuy: '',
        discount_buy: '',
        enable_rent: true,
        pricerent: '',
        discount_rent: '',
        enable_forward: true,
        pricefw: '',
        discount_fw: ''
    });

    const isDomainReady = isValidDomainFormat(subForm.domain_name);

    useEffect(() => {
        getAuthConfig()
            .then(data => {
                if (data && (data.success || data.authEnabled !== undefined)) {
                    setAuthConfig(data);
                    setAuthConfigLoaded(true);
                }
            })
            .catch(() => {
                setAuthConfigLoaded(true);
            });
    }, []);

    const rentUntil = domain?.rent_until ? new Date(domain.rent_until) : null;
    const forwardUntil = domain?.forward_until ? new Date(domain.forward_until) : null;
    const now = new Date();
    
    let busyUntil = null;
    if (rentUntil && rentUntil > now) busyUntil = rentUntil;
    if (forwardUntil && forwardUntil > now) {
        if (!busyUntil || forwardUntil > busyUntil) busyUntil = forwardUntil;
    }

    // Immediately focus on domain input when Sell form opens (desktop only, to avoid pulling up mobile keyboard)
    useEffect(() => {
        if (!isPurchase && !busyUntil && !subSuccess) {
            if (typeof window !== 'undefined' && window.innerWidth < 768) return;
            const timer = setTimeout(() => {
                if (domainInputRef.current) {
                    domainInputRef.current.focus();
                }
            }, 60);
            return () => clearTimeout(timer);
        }
    }, [isPurchase, busyUntil, subSuccess, userToken]);

    const [showAiConfirmModal, setShowAiConfirmModal] = useState(false);

    // AI-powered Auto-fill: Generates marketing pitch via Gemini if enabled, or falls back to demo presets.
    // Calculates length-based tiered pricing automatically.
    const executeAutoFill = async () => {
        setShowAiConfirmModal(false);
        const clean = extractCleanDomain(subForm.domain_name) || (subForm.domain_name || '').trim().toLowerCase() || 'mybrand.com';
        const { buyPrice, rentPrice, fwPrice } = calculateDomainLengthPrices(clean, authConfig.minPrices);

        setIsAutoFilling(true);
        setSubError('');
        setAiNotice('');

        let aiDescription = null;
        try {
            const token = userToken || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            const res = await fetch('/api/ai/autofill-domain', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ domain_name: clean })
            });

            const data = await res.json();
            if (data && data.success && data.description) {
                aiDescription = data.description;
            } else if (data && data.reason === 'rate_limit_exceeded') {
                setSubError(`AI daily limit reached (${data.usage || 10}/${data.limit || 10}). Loaded presets.`);
            }
        } catch (err) {
            console.warn('AI autofill fallback error:', err);
        } finally {
            setIsAutoFilling(false);
        }

        const fallbackDescription = 'Premium high-value domain suitable for technology, finance, or modern digital services.';
        setSubForm(prev => ({
            ...prev,
            domain_name: clean,
            description: aiDescription || fallbackDescription,
            enable_buy: true,
            pricebuy: String(buyPrice),
            discount_buy: '',
            enable_rent: true,
            pricerent: String(rentPrice),
            discount_rent: '',
            enable_forward: true,
            pricefw: String(fwPrice),
            discount_fw: ''
        }));
        setSubError('');
    };

    const handleAutoFillClick = () => {
        // If there's already a description or customized details, ask for confirmation
        if (subForm.description && subForm.description.trim().length > 0) {
            setShowAiConfirmModal(true);
        } else {
            executeAutoFill();
        }
    };

    const handleSubmitDomain = async (e) => {
        e.preventDefault();
        setSubError('');
        setDomainFieldError('');

        const triggerDomainError = (msg) => {
            setDomainFieldError(msg);
            setDomainShake(true);
            setTimeout(() => setDomainShake(false), 800);
            if (domainInputRef.current) {
                domainInputRef.current.focus();
                domainInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        };

        const cleanName = extractCleanDomain(subForm.domain_name);
        if (!cleanName || !isValidDomainFormat(cleanName)) {
            triggerDomainError('Please enter a valid domain name (e.g. brand.com)');
            return;
        }

        if (!subForm.enable_buy && !subForm.enable_rent && !subForm.enable_forward) {
            setSubError('Please select at least one format (Sale, Rent, or Forwarding)');
            return;
        }

        const minPrices = authConfig.minPrices || { buy: 100, rent: 50, forward: 10 };

        if (subForm.enable_buy) {
            const p = Number(subForm.pricebuy);
            if (isNaN(p) || p < minPrices.buy) {
                setSubError(`Minimum purchase price is $${minPrices.buy}`);
                return;
            }
            if (subForm.discount_buy !== '') {
                const d = Number(subForm.discount_buy);
                if (isNaN(d) || d < 0 || d > 50) {
                    setSubError('Sale discount cannot exceed 50%');
                    return;
                }
            }
        }

        if (subForm.enable_rent) {
            const p = Number(subForm.pricerent);
            if (isNaN(p) || p < minPrices.rent) {
                setSubError(`Minimum rent price is $${minPrices.rent}/month`);
                return;
            }
            if (subForm.discount_rent !== '') {
                const d = Number(subForm.discount_rent);
                if (isNaN(d) || d < 0 || d > 50) {
                    setSubError('Rent discount cannot exceed 50%');
                    return;
                }
            }
        }

        if (subForm.enable_forward) {
            const p = Number(subForm.pricefw);
            if (isNaN(p) || p < minPrices.forward) {
                setSubError(`Minimum forwarding price is $${minPrices.forward}/day`);
                return;
            }
            if (subForm.discount_fw !== '') {
                const d = Number(subForm.discount_fw);
                if (isNaN(d) || d < 0 || d > 50) {
                    setSubError('Forwarding discount cannot exceed 50%');
                    return;
                }
            }
        }

        const effectiveToken = userToken || (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
        if (!effectiveToken) {
            setSubError('Please log in with Telegram to list a domain');
            return;
        }

        setSubmittingDomain(true);
        try {
            const response = await fetch('/api/domains/submit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${effectiveToken}`
                },
                body: JSON.stringify({
                    domain_name: cleanName,
                    description: subForm.description.trim(),
                    enable_buy: subForm.enable_buy,
                    pricebuy: subForm.enable_buy ? Number(subForm.pricebuy) : null,
                    discount_buy: subForm.discount_buy ? Number(subForm.discount_buy) : null,
                    enable_rent: subForm.enable_rent,
                    pricerent: subForm.enable_rent ? Number(subForm.pricerent) : null,
                    discount_rent: subForm.discount_rent ? Number(subForm.discount_rent) : null,
                    enable_forward: subForm.enable_forward,
                    pricefw: subForm.enable_forward ? Number(subForm.pricefw) : null,
                    discount_fw: subForm.discount_fw ? Number(subForm.discount_fw) : null
                })
            });

            const data = await response.json();
            if (response.ok && data.success) {
                setSubSuccess(true);
                if (onSubmitted) {
                    try { onSubmitted(data); } catch (e) {}
                }
            } else {
                if (response.status === 403 && (data.disabled || data.session_terminated)) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('telegram_user');
                    localStorage.removeItem('telegramUser');
                    window.dispatchEvent(new Event('domainer_auth_change'));
                    window.dispatchEvent(new Event('authChange'));
                }
                const errMessage = data.error || 'Failed to submit domain for review';
                if (errMessage.toLowerCase().includes('already be listed') || errMessage.toLowerCase().includes('under moderation') || errMessage.toLowerCase().includes('domain')) {
                    triggerDomainError(errMessage);
                } else {
                    setSubError(errMessage);
                }
            }
        } catch (err) {
            setSubError('Network error while submitting domain. Please try again.');
        } finally {
            setSubmittingDomain(false);
        }
    };

    const effectiveBot = authConfig.botUsername || telegramBotUsername;
    const botLoginUrl = effectiveBot ? `https://t.me/${effectiveBot}?start=sell` : telegramContactLink;

    const hasRent = Boolean(domain?.pricerent && Number(domain.pricerent) > 0);
    const hasForward = Boolean(domain?.pricefw && Number(domain.pricefw) > 0);
    const canSwitchToRent = hasRent ? onSwitchToRent : undefined;
    const canSwitchToForward = hasForward ? onSwitchToForward : undefined;

    return (
        <div className="relative max-w-lg md:max-w-xl w-full animate-modal-strict">
            {/* Floating Domain Title and Close button */}
            <div className="flex items-center justify-between gap-2.5 sm:gap-3 mb-2.5 px-1 sm:px-2">
                {domain?.name ? (
                    <div className="flex items-center gap-2 min-w-0 max-w-[calc(100%-48px)]">
                        <span className="text-[10px] sm:text-xs font-bold tracking-wider uppercase text-neutral-400 bg-white/[0.08] border border-white/[0.12] px-2 py-0.5 rounded-md shrink-0">Order</span>
                        <span className="text-base sm:text-xl font-bold font-display text-white truncate domain-spotlight-text">
                            {domain.name}
                        </span>
                    </div>
                ) : null}

                {onClose && (
                    <button 
                        type="button"
                        onClick={onClose}
                        className="ml-auto w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-neutral-900/90 hover:bg-neutral-800 text-neutral-400 hover:text-white border border-white/[0.14] hover:border-white/[0.3] flex items-center justify-center transition-all duration-200 active:scale-90 shadow-lg cursor-pointer group shrink-0"
                        title="Close"
                        aria-label="Close modal"
                    >
                        <CrossIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform duration-200 group-hover:rotate-90 group-hover:scale-110" />
                    </button>
                )}
            </div>

            {/* Bookmark tabs sticking out of the modal window */}
            <div className="relative z-30">
                <FormTabs 
                    activeTab={isPurchase ? "buy" : "sell"} 
                    onSwitchToBuy={undefined}
                    onSwitchToRent={canSwitchToRent} 
                    onSwitchToForward={canSwitchToForward} 
                />
            </div>

            {/* Ambient floating glow behind the modal */}
            <div className="absolute -inset-3 top-16 bg-gradient-to-b from-white/[0.08] via-primary-500/[0.04] to-transparent rounded-[32px] blur-2xl -z-10 pointer-events-none opacity-80" />

            <div className="bg-neutral-950/95 backdrop-blur-3xl rounded-3xl border border-white/[0.16] ring-1 ring-white/[0.08] max-h-[85vh] sm:max-h-[82vh] overflow-y-auto modal-scrollbar shadow-[0_30px_90px_rgba(0,0,0,0.95),0_10px_30px_rgba(0,0,0,0.7),inset_0_1px_1px_rgba(255,255,255,0.22)] relative z-10">
                {/* Top specular edge reflection */}
                <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none rounded-t-3xl z-10" />

                <div className="p-5 sm:p-7 md:p-8">
                    
                    {busyUntil ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <h4 className="text-xl font-bold text-white mb-2">Domain is Busy</h4>
                            <p className="text-neutral-400 mb-6">The domain <span className="text-primary-400 font-semibold">{domain?.name}</span> is currently occupied until {busyUntil.toLocaleDateString()}. Notify me when it becomes available?</p>
                            <a 
                                href={process.env.REACT_APP_TELEGRAM_CONTACT || (telegramBotUsername ? `https://t.me/${telegramBotUsername}?start=notify_${domain?.id}` : '#')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-6 py-2.5 bg-neutral-900 hover:bg-neutral-800 text-white font-medium rounded-xl transition-colors border border-white/[0.12]"
                            >
                                Notify me
                            </a>
                        </div>
                    ) : showPaymentOptions && invoiceData ? (
                        <PaymentModalView
                            domain={domain}
                            action="buy"
                            durationText="Permanent domain purchase"
                            amount={invoiceData.amount}
                            invoiceData={invoiceData}
                            telegramBotUsername={telegramBotUsername}
                            onBack={() => { setShowPaymentOptions(false); setInvoiceData(null); }}
                        />
                    ) : (
                    <div>
                        {isPurchase ? (
                            <>
                                <p className="text-neutral-300 mb-6">
                                    Purchase <span className="text-primary-400 font-semibold">{domain?.name || 'this domain'}</span> for a one-time payment.
                                </p>
                                
                                <div className="space-y-4">
                                    <button 
                                        onClick={handleGenerateInvoice}
                                        disabled={isGeneratingInvoice}
                                        className="w-full bg-gradient-to-r from-sky-500 via-sky-600 to-blue-600 hover:from-sky-400 hover:via-sky-500 hover:to-blue-500 text-white font-semibold py-3.5 px-5 rounded-xl transition-all duration-200 border border-sky-400/30 hover:border-sky-300/50 shadow-[0_10px_25px_-5px_rgba(14,165,233,0.35),0_4px_10px_rgba(0,0,0,0.4),inset_0_1px_1px_rgba(255,255,255,0.35)] active:scale-[0.99] flex flex-col items-center justify-center min-h-[52px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isGeneratingInvoice ? (
                                            <span>Creating invoice...</span>
                                        ) : domain?.discount_buy > 0 ? (
                                            <>
                                                <span className="text-sm font-bold">Continue to Payment ${(domain?.pricebuy * (1 - domain.discount_buy / 100)).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                                <span className="text-xs line-through opacity-75 mt-0.5">${domain?.pricebuy?.toLocaleString()}</span>
                                            </>
                                        ) : (
                                            <span className="text-sm font-bold">Continue to Payment ${domain?.pricebuy?.toLocaleString() || '0'}</span>
                                        )}
                                    </button>

                                    {submitError && (
                                        <div className="text-red-400 text-sm text-center bg-red-400/10 p-3 rounded-lg mt-4">{submitError}</div>
                                    )}
                                </div>
                            </>
                        ) : subSuccess ? (
                            // Success state after submitting domain
                            <div className="text-center py-6 space-y-4">
                                <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/10">
                                    <CheckCircleIcon className="w-9 h-9" />
                                </div>
                                <h3 className="text-xl font-bold text-white">Submission Received!</h3>
                                <p className="text-sm text-neutral-300 leading-relaxed">
                                    Your domain submission for <span className="text-primary-400 font-semibold">{subForm.domain_name}</span> has been successfully sent to the administrator for review.
                                </p>
                                <div className="p-4 rounded-xl bg-dark-200/60 border border-white/10 text-xs text-neutral-400 text-left space-y-2">
                                    <p className="font-semibold text-white">What happens next?</p>
                                    <p>• Administrator reviews your domain details.</p>
                                    <p>• Once approved, you will receive designated Cloudflare nameservers.</p>
                                    <p>• Set NS records at your registrar within 48 hours to activate.</p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-semibold transition-colors"
                                >
                                    Got it
                                </button>
                            </div>
                        ) : userToken ? (
                            // When auth is enabled and user is logged in: unified Add Domain Form with progressive expansion
                            <form onSubmit={handleSubmitDomain} className="space-y-3">
                                {/* Field 1: Domain Name (always visible first) with URL extractor */}
                                <div className="space-y-1">
                                    <div className={`relative transition-all ${domainShake ? 'animate-error-shake' : ''}`}>
                                        <input
                                            ref={domainInputRef}
                                            type="text"
                                            id="sub_domain_name"
                                            name="domain_name"
                                            value={subForm.domain_name}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                // If user pastes a full URL or protocol, extract clean domain immediately
                                                if (raw.includes('://') || (raw.includes('/') && raw.length > 5) || raw.includes('?')) {
                                                    const clean = extractCleanDomain(raw);
                                                    setSubForm({ ...subForm, domain_name: clean || raw });
                                                } else {
                                                    setSubForm({ ...subForm, domain_name: raw });
                                                }
                                                if (domainFieldError) setDomainFieldError('');
                                                if (subError) setSubError('');
                                            }}
                                            onBlur={() => {
                                                const clean = extractCleanDomain(subForm.domain_name);
                                                if (clean && clean !== subForm.domain_name) {
                                                    setSubForm(prev => ({ ...prev, domain_name: clean }));
                                                }
                                            }}
                                            placeholder=" "
                                            required
                                            className={`peer block w-full px-3.5 py-2.5 text-sm text-white bg-neutral-900/90 rounded-xl border appearance-none focus:outline-none transition-all placeholder-transparent ${
                                                domainFieldError 
                                                     ? 'border-red-500 ring-2 ring-red-500/30 bg-red-500/5' 
                                                     : 'border-white/[0.12] focus:ring-1 focus:ring-white/30 focus:border-white/40'
                                            }`}
                                        />
                                        <label
                                            htmlFor="sub_domain_name"
                                            className={`absolute start-3 -top-2.5 z-10 px-1.5 text-xs duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-sm peer-placeholder-shown:bg-transparent peer-focus:-top-2.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:bg-neutral-950 ${
                                                domainFieldError 
                                                     ? 'text-red-400 peer-focus:text-red-400' 
                                                     : 'text-neutral-400 peer-placeholder-shown:text-neutral-500 peer-focus:text-white'
                                            }`}
                                        >
                                            Domain Name (e.g. brand.com) *
                                        </label>
                                    </div>
                                    {domainFieldError && (
                                        <div className="flex items-start gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-xs font-medium animate-fadeIn">
                                            <WarningIcon className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                            <span className="leading-snug">{domainFieldError}</span>
                                        </div>
                                    )}
                                    {!isDomainReady && !domainFieldError && (
                                        <p className="text-[11px] text-neutral-500 px-1 pt-0.5">
                                            Enter your domain (e.g. brand.com) to configure listing options.
                                        </p>
                                    )}
                                </div>

                                {/* Progressive Expansion Container: Reveals rest of form smoothly once domain is valid */}
                                <div className={`transition-all duration-500 ease-in-out origin-top overflow-hidden space-y-4 ${
                                    isDomainReady 
                                        ? 'max-h-[1600px] opacity-100 translate-y-0 pointer-events-auto pt-1' 
                                        : 'max-h-0 opacity-0 -translate-y-2 pointer-events-none'
                                }`}>
                                    {aiNotice && (
                                        <div className="px-3 py-2 rounded-xl bg-primary-500/15 border border-primary-500/30 text-primary-300 text-xs font-medium flex items-center gap-2 animate-fadeIn">
                                            <ZapIcon className="w-3.5 h-3.5 text-primary-400 shrink-0" />
                                            <span>{aiNotice}</span>
                                        </div>
                                    )}

                                    {/* Description field with Magic Wand Auto-fill button in the top-right corner */}
                                    <div className="relative pt-1">
                                        <textarea
                                            id="sub_description"
                                            name="description"
                                            rows={4}
                                            maxLength={250}
                                            disabled={isAutoFilling}
                                            value={subForm.description}
                                            onChange={(e) => setSubForm({ ...subForm, description: e.target.value })}
                                            placeholder=" "
                                            className={`peer block w-full px-3.5 pr-14 py-2.5 min-h-[90px] text-sm text-white bg-neutral-900/90 rounded-xl border border-white/[0.12] appearance-none focus:outline-none focus:ring-1 focus:ring-white/30 focus:border-white/40 transition-all placeholder-transparent resize-y ${
                                                isAutoFilling ? 'opacity-60 cursor-not-allowed bg-neutral-900/50' : ''
                                            }`}
                                        />
                                        <label
                                            htmlFor="sub_description"
                                            className="absolute start-3 -top-1.5 z-10 px-1.5 text-xs text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-3.5 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-sm peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-1.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-white peer-focus:bg-neutral-950"
                                        >
                                            Short Description ({subForm.description.length}/250)
                                        </label>

                                        {/* Auto-fill button in upper right corner of description */}
                                        <button
                                            type="button"
                                            disabled={isAutoFilling}
                                            onClick={handleAutoFillClick}
                                            className="absolute top-2 right-2 z-10 h-7 px-2 rounded-lg flex items-center gap-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/35 transition-all hover:scale-[1.03] active:scale-95 disabled:opacity-50 cursor-pointer shadow-sm group"
                                            title="Auto-fill description and suggested pricing"
                                            aria-label="Auto-fill description and suggested pricing"
                                        >
                                            {isAutoFilling ? (
                                                <>
                                                    <div className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                                                    <span className="text-[11px] font-bold text-amber-300 tracking-wide">Auto</span>
                                                </>
                                            ) : (
                                                <>
                                                    <WandIcon className="w-3.5 h-3.5 text-amber-400 group-hover:rotate-12 transition-transform duration-200" />
                                                    <span className="text-[11px] font-bold text-amber-300 tracking-wide">Auto</span>
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    <div className="space-y-2.5 pt-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">
                                                Listing Formats:
                                            </p>
                                        </div>

                                        {/* Option 1: Buy */}
                                        <div className={`p-3 rounded-xl border transition-colors space-y-2 ${subForm.enable_buy ? 'bg-primary-500/10 border-primary-500/30' : 'bg-neutral-900/60 border-white/[0.08]'}`}>
                                            <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={subForm.enable_buy}
                                                    onChange={(e) => setSubForm({ ...subForm, enable_buy: e.target.checked })}
                                                    className="w-4 h-4 rounded text-primary-500 focus:ring-0 bg-neutral-900 border-white/20 cursor-pointer"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <BuyIcon className="w-4 h-4 text-primary-400" />
                                                    <span className="text-sm font-semibold text-white">Buy / Instant Purchase</span>
                                                </div>
                                                <span className="text-[10px] text-neutral-400 ml-auto">min. ${authConfig.minPrices?.buy || 100}</span>
                                            </label>
                                            {subForm.enable_buy && (
                                                <div className="grid grid-cols-2 gap-2 pt-2 pl-6">
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            id="sub_pricebuy"
                                                            min={authConfig.minPrices?.buy || 100}
                                                            value={subForm.pricebuy}
                                                            onChange={(e) => setSubForm({ ...subForm, pricebuy: e.target.value })}
                                                            placeholder=" "
                                                            required
                                                            className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 focus:outline-none transition-all placeholder-transparent"
                                                        />
                                                        <label
                                                            htmlFor="sub_pricebuy"
                                                            className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-white peer-focus:bg-neutral-950"
                                                        >
                                                            Price ($ USD) *
                                                        </label>
                                                    </div>
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            id="sub_discount_buy"
                                                            min="0"
                                                            max="50"
                                                            value={subForm.discount_buy}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (v === '' || (Number(v) >= 0 && Number(v) <= 50)) {
                                                                    setSubForm({ ...subForm, discount_buy: v });
                                                                }
                                                            }}
                                                            placeholder=" "
                                                            className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 focus:outline-none transition-all placeholder-transparent"
                                                        />
                                                        <label
                                                            htmlFor="sub_discount_buy"
                                                            className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-white peer-focus:bg-neutral-950"
                                                        >
                                                            Discount (% max 50)
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Option 2: Rent */}
                                        <div className={`p-3 rounded-xl border transition-colors space-y-2 ${subForm.enable_rent ? 'bg-purple-500/10 border-purple-500/30' : 'bg-neutral-900/60 border-white/[0.08]'}`}>
                                            <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={subForm.enable_rent}
                                                    onChange={(e) => setSubForm({ ...subForm, enable_rent: e.target.checked })}
                                                    className="w-4 h-4 rounded text-purple-500 focus:ring-0 bg-neutral-900 border-white/20 cursor-pointer"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <RentIcon className="w-4 h-4 text-purple-400" />
                                                    <span className="text-sm font-semibold text-white">Rent / Monthly Lease</span>
                                                </div>
                                                <span className="text-[10px] text-neutral-400 ml-auto">min. ${authConfig.minPrices?.rent || 50}/mo</span>
                                            </label>
                                            {subForm.enable_rent && (
                                                <div className="grid grid-cols-2 gap-2 pt-2 pl-6">
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            id="sub_pricerent"
                                                            min={authConfig.minPrices?.rent || 50}
                                                            value={subForm.pricerent}
                                                            onChange={(e) => setSubForm({ ...subForm, pricerent: e.target.value })}
                                                            placeholder=" "
                                                            required
                                                            className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 focus:outline-none transition-all placeholder-transparent"
                                                        />
                                                        <label
                                                            htmlFor="sub_pricerent"
                                                            className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-white peer-focus:bg-neutral-950"
                                                        >
                                                            Price ($/month) *
                                                        </label>
                                                    </div>
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            id="sub_discount_rent"
                                                            min="0"
                                                            max="50"
                                                            value={subForm.discount_rent}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (v === '' || (Number(v) >= 0 && Number(v) <= 50)) {
                                                                    setSubForm({ ...subForm, discount_rent: v });
                                                                }
                                                            }}
                                                            placeholder=" "
                                                            className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 focus:outline-none transition-all placeholder-transparent"
                                                        />
                                                        <label
                                                            htmlFor="sub_discount_rent"
                                                            className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-white peer-focus:bg-neutral-950"
                                                        >
                                                            Discount (% max 50)
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Option 3: Forwarding */}
                                        <div className={`p-3 rounded-xl border transition-colors space-y-2 ${subForm.enable_forward ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-neutral-900/60 border-white/[0.08]'}`}>
                                            <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                                <input
                                                    type="checkbox"
                                                    checked={subForm.enable_forward}
                                                    onChange={(e) => setSubForm({ ...subForm, enable_forward: e.target.checked })}
                                                    className="w-4 h-4 rounded text-emerald-500 focus:ring-0 bg-neutral-900 border-white/20 cursor-pointer"
                                                />
                                                <div className="flex items-center gap-2">
                                                    <ForwardIcon className="w-4 h-4 text-emerald-400" />
                                                    <span className="text-sm font-semibold text-white">Forwarding / Redirect</span>
                                                </div>
                                                <span className="text-[10px] text-neutral-400 ml-auto">min. ${authConfig.minPrices?.forward || 10}/day</span>
                                            </label>
                                            {subForm.enable_forward && (
                                                <div className="grid grid-cols-2 gap-2 pt-2 pl-6">
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            id="sub_pricefw"
                                                            min={authConfig.minPrices?.forward || 10}
                                                            value={subForm.pricefw}
                                                            onChange={(e) => setSubForm({ ...subForm, pricefw: e.target.value })}
                                                            placeholder=" "
                                                            required
                                                            className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 focus:outline-none transition-all placeholder-transparent"
                                                        />
                                                        <label
                                                            htmlFor="sub_pricefw"
                                                            className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-white peer-focus:bg-neutral-950"
                                                        >
                                                            Price ($/day) *
                                                        </label>
                                                    </div>
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            id="sub_discount_fw"
                                                            min="0"
                                                            max="50"
                                                            value={subForm.discount_fw}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (v === '' || (Number(v) >= 0 && Number(v) <= 50)) {
                                                                    setSubForm({ ...subForm, discount_fw: v });
                                                                }
                                                            }}
                                                            placeholder=" "
                                                            className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-white/40 focus:ring-1 focus:ring-white/30 focus:outline-none transition-all placeholder-transparent"
                                                        />
                                                        <label
                                                            htmlFor="sub_discount_fw"
                                                            className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-white peer-focus:bg-neutral-950"
                                                        >
                                                            Discount (% max 50)
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {subError && (
                                        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs text-center font-medium">
                                            {subError}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={submittingDomain}
                                        className="w-full bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-400 hover:via-amber-500 hover:to-orange-500 text-white font-semibold py-3.5 px-5 rounded-xl transition-all duration-200 border border-amber-400/30 hover:border-amber-300/50 shadow-[0_10px_25px_-5px_rgba(245,158,11,0.35),0_4px_10px_rgba(0,0,0,0.4),inset_0_1px_1px_rgba(255,255,255,0.35)] active:scale-[0.99] flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer text-sm font-bold min-h-[50px]"
                                    >
                                        {submittingDomain ? 'Submitting for Review...' : 'Submit Domain for Review'}
                                    </button>
                                </div>
                            </form>
                        ) : (!authConfig.authEnabled && authConfigLoaded) ? (
                            // When telegram_user_auth_secret is NOT set: standard contact link
                            <>
                                <p className="text-neutral-300 mb-6">
                                    Interested in selling your domain? Contact us directly through Telegram to discuss listing your domain on our platform.
                                </p>
                                
                                <div className="space-y-4">
                                    <a 
                                        href={telegramContactLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 w-full text-center bg-primary-500 hover:bg-primary-600 text-white py-3 px-4 font-semibold rounded-lg transition-colors duration-200"
                                    >
                                        <MessageIcon className="w-4 h-4" />
                                        <span>Contact via Telegram</span>
                                    </a>
                                    
                                    <div className="pt-4 border-t border-dark-300">
                                        <p className="text-neutral-400 text-sm text-center">
                                            Our team will help you set up your domain for sale and guide you through our listing process.
                                        </p>
                                    </div>
                                </div>
                            </>
                        ) : (
                            // When auth is enabled but user is not logged in: bot login prompt
                            <div className="space-y-5 py-2">
                                <div className="text-center space-y-2">
                                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500/20 via-indigo-500/20 to-purple-500/20 border border-primary-500/30 rounded-2xl flex items-center justify-center mx-auto text-primary-400 mb-3 shadow-lg shadow-primary-500/10">
                                        <GlobeIcon className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white">List Your Domain</h3>
                                    <p className="text-sm text-neutral-300 leading-relaxed max-w-sm mx-auto">
                                        To list your domain for sale, rent, or traffic forwarding, authenticate with our Telegram bot.
                                    </p>
                                </div>

                                {/* Three listing formats badges with centralized icons */}
                                <div className="grid grid-cols-3 gap-2.5 py-1">
                                    <div className="flex flex-col items-center p-3 rounded-xl bg-neutral-900/80 border border-white/[0.08] hover:border-white/[0.2] transition-all text-center">
                                        <BuyIcon className="w-5 h-5 text-primary-400 mb-1.5" />
                                        <span className="text-xs font-semibold text-white">Sale</span>
                                        <span className="text-[10px] text-neutral-400">Direct transfer</span>
                                    </div>
                                    <div className="flex flex-col items-center p-3 rounded-xl bg-neutral-900/80 border border-white/[0.08] hover:border-white/[0.2] transition-all text-center">
                                        <RentIcon className="w-5 h-5 text-purple-400 mb-1.5" />
                                        <span className="text-xs font-semibold text-white">Rent</span>
                                        <span className="text-[10px] text-neutral-400">Monthly lease</span>
                                    </div>
                                    <div className="flex flex-col items-center p-3 rounded-xl bg-neutral-900/80 border border-white/[0.08] hover:border-white/[0.2] transition-all text-center">
                                        <ForwardIcon className="w-5 h-5 text-emerald-400 mb-1.5" />
                                        <span className="text-xs font-semibold text-white">Forward</span>
                                        <span className="text-[10px] text-neutral-400">Traffic redirect</span>
                                    </div>
                                </div>

                                <div className="space-y-3 pt-1">
                                    <a
                                        href={botLoginUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold border border-primary-400/30 shadow-md active:scale-[0.99] transition-all text-sm group cursor-pointer"
                                    >
                                        <RobotIcon className="w-4 h-4 transition-transform group-hover:scale-110" />
                                        <span>Log in via Telegram Bot</span>
                                        <ExternalLinkIcon className="w-3.5 h-3.5 opacity-70 ml-0.5" />
                                    </a>

                                    <p className="text-xs text-neutral-400 text-center leading-relaxed">
                                        The bot will provide a secure direct link with your personal access token to list domains.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                </div>
                
                <FormSecurityNotice />
            </div>

            {/* Auto-fill Confirmation Card (100% solid, non-transparent background) */}
            {showAiConfirmModal && (
                <div className="absolute inset-x-4 top-20 z-50 flex justify-center animate-fadeIn pointer-events-auto">
                    <div 
                        style={{ backgroundColor: '#101014', opacity: 1 }}
                        className="bg-neutral-950 border border-amber-500/40 rounded-2xl p-4 max-w-[340px] w-full shadow-[0_25px_70px_rgba(0,0,0,1),0_0_0_1px_rgba(255,255,255,0.08)] space-y-3 animate-modal-strict relative overflow-hidden"
                    >
                        {/* 100% solid opaque background layer to prevent any transparency */}
                        <div className="absolute inset-0 bg-[#101014] pointer-events-none -z-10" style={{ backgroundColor: '#101014' }} />
                        <div className="flex items-start gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0 mt-0.5 shadow-sm">
                                <WandIcon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h4 className="text-xs sm:text-sm font-semibold text-white leading-tight">
                                    Replace description & prices?
                                </h4>
                                <p className="text-[11px] text-neutral-400 mt-1 leading-snug">
                                    Current text and prices will be replaced with suggested listing details.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.08]">
                            <button
                                type="button"
                                onClick={() => setShowAiConfirmModal(false)}
                                className="px-2.5 py-1 rounded-lg text-xs font-medium text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={executeAutoFill}
                                className="px-3 py-1 rounded-lg text-xs font-semibold bg-amber-500 hover:bg-amber-400 active:scale-95 text-neutral-950 shadow-md shadow-amber-500/20 transition-all cursor-pointer flex items-center gap-1.5"
                            >
                                <WandIcon className="w-3.5 h-3.5" />
                                <span>Auto-fill</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactForm;